import React, { useMemo, useState, useRef } from 'react';
import { 
  View, 
  Text, 
  TextInput, 
  TouchableOpacity, 
  StyleSheet, 
  Modal,
  ScrollView,
  Platform,
} from 'react-native';
import { IColumnConfig, IFilterModel, ISortModel } from '@/types/admin/dataTable';
import { DESKTOP_STYLES, DESKTOP_COLORS } from '@/constants/admin';
import { SVG_COMPONENTS } from '@/assets/admin';
import { COLORS } from '@/constants/residents';
import { AnchoredDropdown } from '@/components/common/admin/desktop/dropdown/AnchoredDropdown';
import { AnchorRect } from '@/hooks/common/useAnchoredPosition';
import { DesktopFilterRow } from './DesktopFilterRow';
import { useDataTableState } from '@/contexts/admin/DataTableContext';

interface DesktopTableFiltersProps<T> {
  columns: IColumnConfig<T>[];
  searchQuery: string;
  onSearchChange?: (query: string) => void;
  searchPlaceholder?: string;
  showSearch?: boolean;
  showSort?: boolean;
  showFilter?: boolean;
  filterModel: IFilterModel;
  onFilterChange: (filterModel: IFilterModel) => void;
  sortModel: ISortModel[];
  onSortChange: (sortModel: ISortModel[]) => void;
  onExport?: () => void;
  showExport?: boolean;
  headerRight?: React.ReactNode;
}

export const DesktopTableFilters = <T,>({
  columns,
  searchQuery,
  onSearchChange,
  searchPlaceholder = 'Search...',
  showSearch = true,
  showSort = true,
  showFilter = true,
  filterModel,
  onFilterChange,
  sortModel,
  onSortChange,
  onExport,
  showExport = false,
  headerRight,
}: DesktopTableFiltersProps<T>) => {
  const { tableKey } = useDataTableState();
  const isFundingTable = tableKey === 'admin:funding';
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showSortModal, setShowSortModal] = useState(false);
  const [openPicker, setOpenPicker] = useState<null | { row: number; kind: 'column' | 'operator' | 'value' }>(null);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [sortAnchor, setSortAnchor] = useState<AnchorRect | null>(null);
  const sortBtnRef = useRef<View | null>(null);
  const sortBtnTestId = useMemo(() => `desktop-table-sort-btn-${Math.random().toString(36).slice(2, 8)}`,[/* once */]);

  const filterableColumns = columns.filter(col => col.filterable);
  const sortableColumns = columns.filter(col => col.sortable);
  
  const activeFiltersCount = filterModel.items?.length || 0;
  const activeSortCount = sortModel.length;

  const columnByKey = useMemo(() => {
    const map: Record<string, IColumnConfig<T>> = {} as any;
    filterableColumns.forEach(c => {
      map[c.key as string] = c;
    });
    return map;
  }, [filterableColumns]);

  const openSortDropdown = () => {
    try {
      // Prefer the web path first for React Native Web
      if (Platform.OS === 'web') {
        const el: any = (globalThis as any)?.document?.querySelector?.(`[data-testid="${sortBtnTestId}"]`);
        if (el && el.getBoundingClientRect) {
          const r = el.getBoundingClientRect();
          setSortAnchor({ x: r.left, y: r.top, w: r.width, h: r.height });
          setShowSortDropdown(true);
          return;
        }
      }

      // Native measure (and fallback if RNW supports it)
      if (sortBtnRef.current) {
        const RN: any = require('react-native');
        const node = RN.findNodeHandle(sortBtnRef.current);
        if (node && RN.UIManager && RN.UIManager.measureInWindow) {
          RN.UIManager.measureInWindow(node, (x: number, y: number, width: number, height: number) => {
            setSortAnchor({ x, y, w: width, h: height });
            setShowSortDropdown(true);
          });
          return;
        }
      }

      // Last resort: open without a measured anchor (dropdown won't show without anchor)
      setShowSortDropdown(true);
    } catch {
      setShowSortDropdown(true);
    }
  };

  const getOperatorsForColumn = (col?: IColumnConfig<T>): Array<{ label: string; value: string }> => {
    // For Funding table, only allow the 'is' operator regardless of column type
    if (isFundingTable) {
      return [
        { label: 'is', value: 'is' },
      ];
    }
    if (!col) return [
      { label: 'contains', value: 'contains' },
      { label: 'is', value: 'is' },
      { label: 'is not', value: 'isNot' },
    ];
    switch (col.filterType) {
      case 'select':
      case 'multiSelect':
        return [
          { label: 'is', value: 'is' },
          { label: 'is not', value: 'isNot' },
        ];
      case 'date':
        return [
          { label: 'on or after', value: 'onOrAfter' },
          { label: 'on or before', value: 'onOrBefore' },
        ];
      case 'boolean':
        return [
          { label: 'is', value: 'is' },
        ];
      case 'text':
      default:
        return [
          { label: 'contains', value: 'contains' },
          { label: 'is', value: 'is' },
          { label: 'is not', value: 'isNot' },
        ];
    }
  };

  const getColumnLabel = (key: string) => {
    const col = columnByKey[key];
    return col ? col.label : key;
  };

  const handleAddFilter = () => {
    const newFilter = {
      field: filterableColumns[0]?.key as string,
      operator: isFundingTable ? 'is' : 'contains',
      value: '',
    };
    
    onFilterChange({
      ...filterModel,
      items: [...(filterModel.items || []), newFilter],
    });
  };

  const handleRemoveFilter = (index: number) => {
    const newItems = [...(filterModel.items || [])];
    newItems.splice(index, 1);
    
    onFilterChange({
      ...filterModel,
      items: newItems,
    });
  };

  const handleFilterChange = (index: number, field: string, value: any) => {
    const newItems = [...(filterModel.items || [])];
    newItems[index] = { ...newItems[index], [field]: value };
    
    onFilterChange({
      ...filterModel,
      items: newItems,
    });
  };

  const handleChangeColumn = (index: number, nextKey: string) => {
    const newItems = [...(filterModel.items || [])];
    const col = columnByKey[nextKey];
    const operators = getOperatorsForColumn(col);
    newItems[index] = { field: nextKey, operator: operators[0].value, value: '' };
    onFilterChange({ ...filterModel, items: newItems });
  };

  const handleChangeOperator = (index: number, op: string) => {
    handleFilterChange(index, 'operator', op);
  };

  const renderFilterModal = () => (
    <Modal
      visible={showFilterModal}
      transparent
      animationType="fade"
      onRequestClose={() => setShowFilterModal(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <View style={styles.modalHeader}>
            <Text style={[styles.modalTitle, DESKTOP_STYLES.primaryText]}>
              Filters
            </Text>
            <TouchableOpacity
              onPress={() => setShowFilterModal(false)}
              style={styles.closeButton}
            >
              <SVG_COMPONENTS.X_CIRCLE width={20} height={20} color={DESKTOP_COLORS.TABLE.TEXT} />
            </TouchableOpacity>
          </View>
          
          <View style={styles.modalBody}>
            {filterModel.items?.map((filter, index) => {
              const currentColumn = columnByKey[filter.field];
              const operatorOptions = getOperatorsForColumn(currentColumn);
              return (
                <View key={index} style={[
                  styles.filterRow,
                  { width: '100%' },
                  openPicker?.row === index ? styles.filterRowActive : undefined
                ]}>
                  <DesktopFilterRow
                    index={index}
                    filter={filter}
                    columnByKey={columnByKey}
                    filterableColumns={filterableColumns}
                    operatorOptions={operatorOptions}
                    onChangeField={(i, key) => handleChangeColumn(i, key)}
                    onChangeOperator={(i, op) => handleChangeOperator(i, op)}
                    onChangeValue={(i, v) => handleFilterChange(i, 'value', v)}
                    onRemove={(i) => handleRemoveFilter(i)}
                    onOpenColumnPicker={(i) => setOpenPicker(prev => (prev && prev.row === i && prev.kind === 'column') ? null : { row: i, kind: 'column' })}
                    onOpenOperatorPicker={(i) => setOpenPicker(prev => (prev && prev.row === i && prev.kind === 'operator') ? null : { row: i, kind: 'operator' })}
                    onOpenValuePicker={(i) => setOpenPicker(prev => (prev && prev.row === i && prev.kind === 'value') ? null : { row: i, kind: 'value' })}
                  />
                  {openPicker?.row === index && openPicker.kind === 'column' && (
                    <View style={styles.pickerPanel}>
                      <ScrollView style={styles.pickerScrollView} nestedScrollEnabled>
                        {filterableColumns.map((col) => (
                          <TouchableOpacity
                            key={col.key as string}
                            style={styles.pickerOption}
                            onPress={() => { handleChangeColumn(index, col.key as string); setOpenPicker(null); }}
                          >
                            <Text style={DESKTOP_STYLES.primaryText}>{col.label}</Text>
                          </TouchableOpacity>
                        ))}
                      </ScrollView>
                    </View>
                  )}
                  {openPicker?.row === index && openPicker.kind === 'operator' && (
                    <View style={styles.pickerPanel}>
                      <ScrollView style={styles.pickerScrollView} nestedScrollEnabled>
                        {operatorOptions.map((op) => (
                          <TouchableOpacity
                            key={op.value}
                            style={styles.pickerOption}
                            onPress={() => { handleChangeOperator(index, op.value); setOpenPicker(null); }}
                          >
                            <Text style={DESKTOP_STYLES.primaryText}>{op.label}</Text>
                          </TouchableOpacity>
                        ))}
                      </ScrollView>
                    </View>
                  )}
                  {openPicker?.row === index && openPicker.kind === 'value' && (
                    <View style={styles.pickerPanel}>
                      <ScrollView style={styles.pickerScrollView} nestedScrollEnabled>
                        {(currentColumn?.filterOptions || []).map((opt) => (
                          <TouchableOpacity
                            key={`${opt.label}-${opt.value}`}
                            style={styles.pickerOption}
                            onPress={() => { handleFilterChange(index, 'value', opt.value); setOpenPicker(null); }}
                          >
                            <Text style={DESKTOP_STYLES.primaryText}>{opt.label}</Text>
                          </TouchableOpacity>
                        ))}
                      </ScrollView>
                    </View>
                  )}
                </View>
              );
            })}
            
            <TouchableOpacity
              onPress={handleAddFilter}
              style={[styles.addButton, DESKTOP_STYLES.secondaryButton]}
            >
              <SVG_COMPONENTS.ADD_BUTTON width={16} height={16} color={DESKTOP_COLORS.BUTTON.SECONDARY_TEXT} />
              <Text style={[styles.addButtonText, { color: DESKTOP_COLORS.BUTTON.SECONDARY_TEXT }]}>
                Add Filter
              </Text>
            </TouchableOpacity>
          </View>
          
          {openPicker && (
            <TouchableOpacity
              style={styles.dropdownDismissOverlay}
              activeOpacity={1}
              onPress={() => setOpenPicker(null)}
            />
          )}
          
          <View style={styles.modalFooter}>
            <TouchableOpacity
              onPress={() => {
                onFilterChange({ items: [] });
                setShowFilterModal(false);
              }}
              style={[styles.button, DESKTOP_STYLES.secondaryButton]}
            >
              <Text style={[styles.buttonText, { color: DESKTOP_COLORS.BUTTON.SECONDARY_TEXT }]}>
                Clear All
              </Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              onPress={() => setShowFilterModal(false)}
              style={[styles.button, DESKTOP_STYLES.primaryButton]}
            >
              <Text style={[styles.buttonText, { color: DESKTOP_COLORS.BUTTON.PRIMARY_TEXT }]}>
                Apply
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  return (
    <View style={styles.container}>
      <View style={styles.leftSection}>
        {showSearch && (
          <View style={styles.searchContainer}>
            <SVG_COMPONENTS.MAGNIFYING_GLASS width={16} height={16} color={DESKTOP_COLORS.TABLE.TEXT_SECONDARY} />
            <TextInput
              style={[styles.searchInput, DESKTOP_STYLES.primaryText, Platform.OS === 'web' ? ({ outlineStyle: 'none' } as any) : null]}
              value={searchQuery}
              onChangeText={onSearchChange}
              placeholder={searchPlaceholder}
              placeholderTextColor={DESKTOP_COLORS.TABLE.TEXT_SECONDARY}
            />
          </View>
        )}
      </View>
      
      <View style={styles.rightSection}>
        {headerRight}
        {showFilter && (
          <TouchableOpacity
            onPress={() => setShowFilterModal(true)}
            style={[styles.filterButton, activeFiltersCount > 0 && styles.filterButtonActive]}
          >
            <SVG_COMPONENTS.FILTER width={16} height={16} color={
              activeFiltersCount > 0 
                ? DESKTOP_COLORS.BUTTON.PRIMARY_BACKGROUND 
                : DESKTOP_COLORS.TABLE.TEXT_SECONDARY
            } />
            <Text style={[
              styles.filterButtonText,
              activeFiltersCount > 0 && styles.filterButtonTextActive
            ]}>
              Filter
            </Text>
            {activeFiltersCount > 0 && (
              <View style={styles.badge}>
                <Text style={styles.badgeText}>{activeFiltersCount}</Text>
              </View>
            )}
          </TouchableOpacity>
        )}
        
        {showSort && (
          <TouchableOpacity
            ref={sortBtnRef as any}
            testID={sortBtnTestId}
            onPress={openSortDropdown}
            style={[styles.filterButton, activeSortCount > 0 && styles.filterButtonActive]}
          >
            <SVG_COMPONENTS.CHEVRON_DOWN width={16} height={16} color={
              activeSortCount > 0 
                ? DESKTOP_COLORS.BUTTON.PRIMARY_BACKGROUND 
                : DESKTOP_COLORS.TABLE.TEXT_SECONDARY
            } />
            <Text style={[
              styles.filterButtonText,
              activeSortCount > 0 && styles.filterButtonTextActive
            ]}>
              Sort
            </Text>
            {activeSortCount > 0 && (
              <View style={styles.badge}>
                <Text style={styles.badgeText}>{activeSortCount}</Text>
              </View>
            )}
          </TouchableOpacity>
        )}
        
        {showExport && (
          <TouchableOpacity
            onPress={onExport}
            style={[styles.button, DESKTOP_STYLES.secondaryButton]}
          >
            <SVG_COMPONENTS.DOWNLOAD width={16} height={16} color={DESKTOP_COLORS.BUTTON.SECONDARY_TEXT} />
            <Text style={[styles.buttonText, { color: DESKTOP_COLORS.BUTTON.SECONDARY_TEXT }]}>
              Export
            </Text>
          </TouchableOpacity>
        )}
      </View>
      
      {renderFilterModal()}

      {/* Sort dropdown (anchored) */}
      <AnchoredDropdown
        isOpen={showSortDropdown}
        anchor={sortAnchor}
        width={240}
        maxHeight={320}
        onRequestClose={() => setShowSortDropdown(false)}
      >
        <View>
          {sortableColumns.map((col) => {
            const isActive = sortModel[0]?.field === (col.key as any);
            const defaultSort = (col as any).defaultSort || 'asc';
            return (
              <TouchableOpacity
                key={`${String(col.key)}-single`}
                style={styles.sortDropdownItem}
                onPress={() => { onSortChange([{ field: col.key as any, sort: defaultSort }]); setShowSortDropdown(false); }}
              >
                <Text style={[styles.sortDropdownText, isActive && styles.sortActive]}>{col.label}</Text>
              </TouchableOpacity>
            );
          })}
        </View>
      </AnchoredDropdown>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: DESKTOP_COLORS.TABLE.HEADER_BACKGROUND,
    borderBottomWidth: 1,
    borderBottomColor: DESKTOP_COLORS.TABLE.BORDER,
  },
  leftSection: {
    flex: 1,
    marginRight: 16,
  },
  rightSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: DESKTOP_COLORS.TABLE.BACKGROUND,
    borderWidth: 1,
    borderColor: DESKTOP_COLORS.TABLE.BORDER,
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    maxWidth: 300,
  },
  searchInput: {
    flex: 1,
    marginLeft: 8,
    fontSize: 14,
    // Web outline is handled by React Native Web automatically
  },
  filterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: DESKTOP_COLORS.TABLE.BORDER,
    backgroundColor: DESKTOP_COLORS.TABLE.BACKGROUND,
    gap: 6,
  },
  filterButtonActive: {
    borderColor:   COLORS.BUTTON_PRIMARY,
    backgroundColor: `${DESKTOP_COLORS.BUTTON.PRIMARY_BACKGROUND}10`,
  },
  filterButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: DESKTOP_COLORS.TABLE.TEXT_SECONDARY,
  },
  filterButtonTextActive: {
    color: DESKTOP_COLORS.TABLE.TEXT,
  },
  badge: {
    backgroundColor: COLORS.BUTTON_PRIMARY,
    borderRadius: 10,
    minWidth: 18,
    height: 18,
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 4,
  },
  badgeText: {
    color: DESKTOP_COLORS.BUTTON.PRIMARY_TEXT,
    fontSize: 11,
    fontWeight: '600',
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    gap: 6,
  },
  buttonText: {
    fontSize: 14,
    fontWeight: '500',
  },
  
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: DESKTOP_COLORS.TABLE.BACKGROUND,
    borderRadius: 8,
    width: '90%',
    maxWidth: 600,
    maxHeight: '80%',
    overflow: 'visible',
    position: 'relative',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: DESKTOP_COLORS.TABLE.BORDER,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  closeButton: {
    padding: 4,
  },
  modalBody: {
    padding: 16,
    maxHeight: 400,
    overflow: 'visible',
    position: 'relative',
    // Ensure dropdown panels render above the local dismiss overlay
    zIndex: 2,
  },
  filterRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 12,
    marginBottom: 12,
    position: 'relative',
  },
  filterRowActive: {
    // Elevate the active row so its dropdown can overlay other rows
    zIndex: 5,
  },
  filterField: {
    flex: 1,
    position: 'relative',
  },
  filterLabel: {
    fontSize: 12,
    marginBottom: 4,
  },
  filterInput: {
    borderWidth: 1,
    borderColor: DESKTOP_COLORS.TABLE.BORDER,
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 6,
    fontSize: 14,
    backgroundColor: DESKTOP_COLORS.TABLE.BACKGROUND,
  },
  removeFilterButton: {
    padding: 8,
    marginBottom: 2,
  },
  pickerPanel: {
    position: 'absolute',
    top: 64,
    left: 0,
    right: 0,
    backgroundColor: DESKTOP_COLORS.TABLE.BACKGROUND,
    borderWidth: 1,
    borderColor: DESKTOP_COLORS.TABLE.BORDER,
    borderRadius: 6,
    zIndex: 1000,
    maxHeight: 300,
    overflow: 'hidden',
  },
  pickerScrollView: {
    maxHeight: 300,
  },
  pickerOption: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: DESKTOP_COLORS.TABLE.BORDER,
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    marginTop: 8,
    gap: 6,
  },
  addButtonText: {
    fontSize: 14,
    fontWeight: '500',
  },
  modalFooter: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 8,
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: DESKTOP_COLORS.TABLE.BORDER,
  },
  dropdownDismissOverlay: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    // Keep this beneath modal body so it doesn't block dropdown clicks
    zIndex: 1,
  },
  sortDropdownItem: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: DESKTOP_COLORS.TABLE.BORDER,
    backgroundColor: DESKTOP_COLORS.TABLE.BACKGROUND,
  },
  sortDropdownText: {
    color: DESKTOP_COLORS.TABLE.TEXT,
    fontSize: 14,
  },
  sortActive: {
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
});
